import { useState, useRef, useEffect, useCallback } from 'react'
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent'
import { registerLlmPreference } from '@/app/actions/llm-preference'
import { useLlmModelStore } from './llm-model-store'
import { getLlmModelOption } from '../const/llm-model-options'

/**
 * LLM 모델 선택 훅
 *
 * 책임:
 * - 드롭다운 열림/닫힘 + 외부 클릭 감지
 * - 선택된 모델을 localStorage에 유지 (llm-model-store)
 * - 세션 시작/모델 변경 시 백엔드에 세션 선호 등록
 *   (Voice Server가 메시지를 중계하므로 session_id 기반으로 사전 등록)
 */
export const useLlmModelSelect = () => {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  const selectedModelKey = useLlmModelStore((state) => state.selectedModelKey)
  const setSelectedModelKey = useLlmModelStore(
    (state) => state.setSelectedModelKey
  )

  const agentId = useRealtimeAgentStore((state) => state.agentId)
  const sessionId = useRealtimeAgentStore((state) => state.sessionId)

  // 세션이 생기거나(재연결 포함) 모델이 바뀌면 선호 등록
  useEffect(() => {
    if (!agentId || !sessionId) return

    const option = getLlmModelOption(selectedModelKey)
    registerLlmPreference(agentId, sessionId, option.provider, option.modelId)
  }, [agentId, sessionId, selectedModelKey])

  // 외부 클릭 감지하여 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const toggleDropdown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen((prev) => !prev)
  }, [])

  const selectModel = useCallback(
    (modelKey: string, e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setSelectedModelKey(modelKey)
      setIsOpen(false)
    },
    [setSelectedModelKey]
  )

  return {
    isOpen,
    selectedModelKey,
    selectRef,
    toggleDropdown,
    selectModel,
  }
}
